import React, { useEffect, useRef, useState } from "react";
import { Button, Modal, Spinner } from "react-bootstrap";
import DataTable from "react-data-table-component";
import ReactPaginate from "react-paginate";
import { debounce } from "lodash";
import * as Api from "../../../General/api";
import * as Common from "../../../General/common";
import ModalLoading from "../../workqueue/Modal/ModalLoading";

/**
 * Reusable Pending CMNs table — used in 3 contexts:
 *  - Facility Dashboard sub-tab:  { facilityId > 0 }
 *  - Provider Dashboard sub-tab:  { physicianId > 0 }
 *  - CMNs main tab (all):         { facilityId = 0, physicianId = 0 }
 *
 * Per spec: all columns sortable. Sort handled client-side (Pending/List endpoint
 * does not yet expose sortColumn/sortDirection params — tracked for future update).
 */
function PendingCmnsTab({ facilityId = 0, physicianId = 0 }) {
  const [items, setItems] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const [showCmnPreview, setShowCmnPreview] = useState(false);
  const [previewCmnId, setPreviewCmnId] = useState(null);
  const [previewBlobUrl, setPreviewBlobUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const previewBlobRef = useRef(null);

  useEffect(() => {
    fetchPendingCmns();
  }, [currentPage, rowsPerPage, facilityId, physicianId]);

  useEffect(() => {
    if (!showCmnPreview || !previewCmnId) {
      return undefined;
    }

    setPreviewLoading(true);
    setPreviewError(null);
    if (previewBlobRef.current) {
      URL.revokeObjectURL(previewBlobRef.current);
      previewBlobRef.current = null;
    }
    setPreviewBlobUrl(null);

    let cancelled = false;
    Api.getRequestBlob(Api.Uri_CmnDocumentPreview + previewCmnId)
      .then((res) => {
        if (cancelled) return;
        const blob = res.data instanceof Blob ? res.data : new Blob([res.data]);
        const ct = (res.headers && res.headers["content-type"]) || blob.type || "";
        if (ct.includes("json") || blob.type === "application/json") {
          return blob.text().then((t) => {
            throw new Error(t || "Preview failed");
          });
        }
        const u = URL.createObjectURL(blob);
        previewBlobRef.current = u;
        setPreviewBlobUrl(u);
      })
      .catch((ex) => {
        if (cancelled) return;
        Common.LogError(ex, "PendingCmnsTab", "loadCmnPreview");
        setPreviewError("Unable to load CMN document preview.");
      })
      .finally(() => {
        if (!cancelled) setPreviewLoading(false);
      });

    return () => {
      cancelled = true;
      if (previewBlobRef.current) {
        URL.revokeObjectURL(previewBlobRef.current);
        previewBlobRef.current = null;
      }
    };
  }, [showCmnPreview, previewCmnId]);

  const closePreview = () => {
    setShowCmnPreview(false);
    setPreviewCmnId(null);
    setPreviewBlobUrl(null);
    setPreviewError(null);
    if (previewBlobRef.current) {
      URL.revokeObjectURL(previewBlobRef.current);
      previewBlobRef.current = null;
    }
  };

  const fetchPendingCmns = (search = "") => {
    setIsLoading(true);
    const qry =
      `?facilityId=${facilityId}` +
      `&physicianId=${physicianId}` +
      `&search=${encodeURIComponent(search)}` +
      `&pageIndex=${currentPage}` +
      `&pageSize=${rowsPerPage}`;
    Api.getRequestQry(Api.Uri_CmnPendingList, qry)
      .then((res) => {
        if (res.data.statusCode === 200 && res.data.result) {
          setTotalRows(res.data.result.totalRows || 0);
          setItems(res.data.result.items || []);
        } else {
          setItems([]);
          setTotalRows(0);
        }
      })
      .catch((ex) => Common.LogError(ex, "PendingCmnsTab", "fetchPendingCmns"))
      .finally(() => setIsLoading(false));
  };

  const handleFilter = debounce((val) => {
    setCurrentPage(1);
    fetchPendingCmns(val);
  }, 800);

  const handlePagination = (page) => setCurrentPage(page.selected + 1);

  const handlePerPage = (e) => {
    setRowsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const openCmnPreview = (cmnId) => {
    setPreviewCmnId(cmnId);
    setShowCmnPreview(true);
  };

  const columns = [
    {
      name: "FC CMN ID",
      sortable: true,
      minWidth: "120px",
      selector: (row) => row.fC_CMNId,
      cell: (row) => (
        <button
          type="button"
          className="btn btn-link p-0"
          onClick={() => openCmnPreview(row.fC_CMNId)}
          title="Preview CMN document"
        >
          {row.fC_CMNId}
        </button>
      ),
    },
    {
      name: "BT CMN ID",
      sortable: true,
      minWidth: "120px",
      selector: (row) => row.bT_CMNKey || "--",
    },
    {
      name: "Resident Name",
      sortable: true,
      minWidth: "160px",
      selector: (row) => Common.replaceNullWithString(row.residentName),
    },
    {
      name: "Provider Name",
      sortable: true,
      minWidth: "160px",
      selector: (row) => row.physicianName,
      cell: (row) =>
        row.physicianName ? (
          <span className="text-primary" style={{ cursor: "default" }} title={row.physicianName}>
            {row.physicianName}
          </span>
        ) : (
          "--"
        ),
    },
    {
      name: "Facility Name",
      sortable: true,
      minWidth: "180px",
      selector: (row) => row.facilityName,
      cell: (row) =>
        row.facilityName ? (
          <span className="text-primary" style={{ cursor: "default" }} title={row.facilityName}>
            {row.facilityName}
          </span>
        ) : (
          "--"
        ),
    },
    {
      name: "Timely Filing",
      sortable: true,
      minWidth: "130px",
      selector: (row) => row.timelyFilingDaysRemaining,
      cell: (row) => {
        const days = row.timelyFilingDaysRemaining;
        if (days == null) return "--";
        if (days <= 0) return <span className="badge badge-danger">{days}d</span>;
        if (days <= 7) return <span className="badge badge-warning">{days}d</span>;
        return <span className="badge badge-success">{days}d</span>;
      },
    },
    {
      name: "Branch",
      sortable: true,
      minWidth: "120px",
      selector: (row) => Common.replaceNullWithString(row.branchName),
    },
  ];

  const CustomPagination = () => {
    const count = Math.ceil(totalRows / rowsPerPage) || 1;
    return (
      <ReactPaginate
        previousLabel=""
        nextLabel=""
        pageCount={count}
        activeClassName="active"
        forcePage={currentPage !== 0 ? currentPage - 1 : 0}
        onPageChange={handlePagination}
        pageClassName="page-item"
        nextLinkClassName="page-link"
        nextClassName="page-item next"
        previousClassName="page-item prev"
        previousLinkClassName="page-link"
        pageLinkClassName="page-link"
        containerClassName="pagination react-paginate justify-content-end my-2 pe-1"
      />
    );
  };

  return (
    <>
      <div className="react-dataTable">
        <DataTable
          noHeader
          striped
          highlightOnHover
          responsive
          pagination
          paginationServer
          subHeader
          columns={columns}
          data={items}
          progressPending={isLoading}
          progressComponent={<ModalLoading />}
          paginationComponent={CustomPagination}
          className="react-dataTable"
          subHeaderComponent={
            <div className="w-100 mb-2">
              <div className="d-flex align-items-center flex-wrap" style={{ gap: "1rem" }}>
                <div className="d-flex align-items-center">
                  <label className="mb-0 mr-2" htmlFor="cmn-search">
                    Search:
                  </label>
                  <input
                    id="cmn-search"
                    type="text"
                    className="form-control form-control-sm"
                    style={{ width: "220px" }}
                    placeholder="CMN ID, resident, facility..."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      handleFilter(e.target.value);
                    }}
                  />
                </div>
                <div className="d-flex align-items-center">
                  <label className="mb-0 mr-2" htmlFor="cmn-per-page">
                    Show
                  </label>
                  <select
                    id="cmn-per-page"
                    className="form-control form-control-sm"
                    style={{ width: "80px" }}
                    value={rowsPerPage}
                    onChange={handlePerPage}
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                  <span className="ml-2 text-muted">entries</span>
                </div>
              </div>
            </div>
          }
        />
      </div>

      <Modal
        size="xl"
        show={showCmnPreview}
        onHide={closePreview}
        backdrop="static"
        keyboard={false}
        centered
        scrollable
        dialogClassName="modal-90w"
      >
        <Modal.Header closeButton>
          <Modal.Title>CMN Document Preview — ID: {previewCmnId}</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ minHeight: "600px", padding: 0 }}>
          {previewLoading && (
            <div className="d-flex justify-content-center align-items-center py-5">
              <Spinner animation="border" variant="primary" />
            </div>
          )}
          {previewError && !previewLoading && (
            <div className="p-4 text-danger text-center">{previewError}</div>
          )}
          {previewBlobUrl && !previewError && !previewLoading && (
            <iframe
              title={`CMN-${previewCmnId}`}
              src={previewBlobUrl}
              style={{ width: "100%", height: "600px", border: "none" }}
            />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="danger" size="sm" onClick={closePreview}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default PendingCmnsTab;
